// Openbook (github.com/veltzer/openbook) tune importer. Each tune is a Mako
// template of LilyPond fragments in a rigid house style; this parser reads
// THAT SUBSET — never general LilyPond — straight into the Score model:
//
//   % if part=='Vars':        title/composer/style metadata (python attrs)
//   % if part=='Chords<V>':   \chordmode — the FORM: bars, repeats/endings,
//                             \myMark sections, \mySegno/\myCoda/D.S., and
//                             the harmony lane
//   % if part=='Voice<V>':    the melody, LilyPond relative pitch entry
//   % if part=='Lyrics<V>':   verse 1 (+ Lyricsmore…, verses 2-3)
//
// The melody fragments carry no \relative wrapper — openbook's build
// template (include/common.ly.mako) wraps EVERY voice as `\relative c'
// { … }`, with no absolute mode anywhere, so that is the one pitch
// convention here. NB the compiled book faithfully renders oddly-entered
// sources (500 Miles High's `d''` pickup really prints at D6, two ledger
// lines up — verified against openbook.pdf page 5); we reproduce the book,
// not a corrected register.

import { chartChordSymbol, chordFromType } from '../chart';
import type { ChordTypeId } from '../chords';
import type {
  FormMeasure,
  HarmonySymbol,
  KeySignature,
  LyricSyllable,
  NoteEvent,
  Score,
  TupletGroup,
  Voice,
  VoiceItem,
} from '../score/types';
import type { Duration, NoteValue } from '../score/duration';
import { NOTE_VALUES, durationQuarters, equalsF, frac } from '../score/duration';
import type { SpelledPitch, Step } from '../score/pitch';
import { midiOf } from '../score/pitch';
import { normalizeScore } from '../score/normalize';
import { uid } from '../song';

// ---------------------------------------------------------------- sections

/** Extract the body of `% if part=='<name>':` … `% endif`. */
export function makoSection(src: string, name: string): string | null {
  const re = new RegExp(`% if part=='${name}':\\s*\\n([\\s\\S]*?)\\n% endif`, 'm');
  const m = src.match(re);
  return m ? m[1] : null;
}

/** attributes['key']='value' pairs from the Vars block. */
export function makoAttributes(src: string): Record<string, string> {
  const vars = makoSection(src, 'Vars') ?? '';
  const out: Record<string, string> = {};
  const re = /attributes\['([^']+)'\]\s*=\s*'((?:\\'|[^'])*)'/g;
  for (let m = re.exec(vars); m; m = re.exec(vars)) out[m[1]] = m[2].replace(/\\'/g, "'");
  return out;
}

/** Version names in add_version order, and the declared default. */
export function makoVersions(src: string): { versions: string[]; def: string } {
  const vars = makoSection(src, 'Vars') ?? '';
  const versions: string[] = [];
  const re = /add_version\('([^']+)'/g;
  for (let m = re.exec(vars); m; m = re.exec(vars)) versions.push(m[1]);
  const def = vars.match(/set_default_version_name\('([^']+)'\)/)?.[1] ?? versions[0] ?? 'Fake';
  return { versions, def };
}

// ------------------------------------------------------------------ chords

/**
 * LilyPond chordmode quality → [catalogue type, iReal-vocabulary display
 * quality]. The display string is what the book PRINTS (Em, F, A13, F+,
 * B7alt, G7/D…); the catalogue type is the four-part voicing it PLAYS as.
 * Longest match first.
 */
const QUALITY_MAP: [string, ChordTypeId, string][] = [
  ['7.3-.5-.9-.11-.13-', 'dom7s5', '7alt'], // the openbook "alt" spelling
  ['m7.5-', 'min7b5', '-7b5'],
  ['7.9-', 'dom7b9', '7b9'],
  ['7.9+', 'dom7s9', '7#9'],
  ['7.5+', 'dom7s5', '7#5'],
  ['7.5-', 'dom7b5', '7b5'],
  ['7.9', 'dom9', '9'],
  ['dim7', 'dim7', 'o7'],
  ['dim', 'dim7', 'o'],
  ['maj9', 'maj9', '^9'],
  ['maj7', 'maj7', '^7'],
  ['maj', 'maj7', '^7'],
  ['m11', 'min9', '-11'],
  ['m9', 'min9', '-9'],
  ['m7', 'min7', '-7'],
  ['m6', 'min6', '-6'],
  ['6.9', 'maj69', '69'],
  ['13', 'dom13', '13'],
  ['11', 'dom9', '11'],
  ['9', 'dom9', '9'],
  ['5+', 'dom7s5', '+'],
  ['7', 'dom7', '7'],
  ['6', 'maj6', '6'],
  ['m', 'min7', '-'], // bare minor triad: plays -7, prints "m"
];

function qualityRow(q: string): [string, ChordTypeId, string] | null {
  for (const row of QUALITY_MAP) if (q === row[0]) return row;
  const head = q.split('.')[0];
  for (const row of QUALITY_MAP) if (head === row[0]) return row;
  return null;
}

export function lilyQuality(q: string): ChordTypeId {
  // Bare major triad: the catalogue is four-part, so a triad must gain a
  // fourth voice. It takes the 7th (matching the iReal parser's bare-triad
  // rule and the bare-minor row below, which plays -7): "G" voices as G∆.
  // The book still PRINTS "G" — see lilyDisplayQuality.
  if (!q) return 'maj7';
  return qualityRow(q)?.[1] ?? 'maj7';
}

/** The quality string the book prints ('' = bare major triad, e.g. "F"). */
export function lilyDisplayQuality(q: string): string {
  if (!q) return '';
  return qualityRow(q)?.[2] ?? '^7';
}

const NOTE_LETTERS = new Set(['a', 'b', 'c', 'd', 'e', 'f', 'g']);

/** "bes" → "Bb"; "fis" → "F#"; "aes"/"as" → "Ab". */
export function lilyRootName(tok: string): string {
  const letter = tok[0].toUpperCase();
  const rest = tok.slice(1);
  if (/^(is)+$/.test(rest)) return letter + '#'.repeat(rest.length / 2);
  if (/^(es|s)(es)*$/.test(rest)) return letter + 'b'.repeat(Math.ceil(rest.length / 2));
  return letter;
}

interface FormEvents {
  mark?: string;
  segno?: boolean;
  coda?: boolean;
  directive?: string;
  openRepeat?: boolean;
  ending?: number;
}

/** A short (pickup) bar's time signature with an integral numerator. */
function sigOfQuarters(q: number, preferDen: number): [number, number] {
  for (const den of [preferDen, 4, 8, 16, 32]) {
    const n = (q * den) / 4;
    if (Math.abs(n - Math.round(n)) < 1e-6 && n >= 1) return [Math.round(n), den];
  }
  return [Math.max(1, Math.round(q)), 4];
}

/**
 * Parse the \chordmode block into FormMeasures: harmony + structure. Chords
 * split into bars at `|`; durations latch like LilyPond's — INCLUDING the
 * `*N` multiplier (500 Miles High's `e1*2:m | g:m` gives g:m two whole bars
 * too; without that the chords run 12 bars against an 18-bar melody). A
 * symbol spanning multiple bars is split at each barline. `s` spacers extend
 * the previous chord, or open the bar as N.C. The structural macros
 * (\myMark, \mySegno, \myCoda, \repeat volta, \alternative) ride the next
 * bar; a \partial pickup keeps the declared meter but carries its sounding
 * duration separately.
 */
export function parseChords(block: string, meter: [number, number]): FormMeasure[] {
  const src = block
    .replace(/%%?[^\n]*/g, '')
    // Glyph marks are navigation: the coda/segno SIGN drawn in the chart.
    // Left to the generic markup→string transform their inner quotes
    // (`#"scripts.coda"`) shatter the tokenizer into garbage directives.
    .replace(/\\mark\s+\\markup\s*\{\s*\\musicglyph\s*#"scripts\.coda"\s*\}/g, '\\myCoda')
    .replace(/\\mark\s+\\markup\s*\{\s*\\musicglyph\s*#"scripts\.segno"\s*\}/g, '\\mySegno')
    .replace(/\\markup\s*\{\s*\\italic\s*\{([^}]*)\}\s*\}/g, '\\markup {$1}')
    .replace(/\\markup\s*\{([^}]*)\}/g, (_, s: string) => `"${s.trim()}"`);
  const tokens = tokenize(src);
  const measures: FormMeasure[] = [];
  const quartersPerBar = (meter[0] * 4) / meter[1];

  let pending: FormEvents = {};
  let symbols: { root: string | null; quality: string; bass: string | null; beatsQ: number; rest?: boolean }[] = [];
  let barQ = 0;
  let lastDurQ = 4;
  let pickupQ: number | null = null;
  const depth: ('plain' | 'repeat' | 'alt' | 'altGroup')[] = [];
  let pendingRepeat = false;
  let pendingAlt = false;
  let altIndex = 0;

  const pushMeasure = () => {
    const pickup = measures.length === 0 && pickupQ != null && pickupQ > 1e-9;
    const short = Math.abs(barQ - quartersPerBar) > 1e-6;
    const sig = short && !pickup ? sigOfQuarters(barQ, meter[1]) : null;
    const den = (sig ?? meter)[1];
    const toUnits = (q: number) => (q * den) / 4;
    const harmony: HarmonySymbol[] = [];
    for (const s of symbols) {
      if (s.root != null) {
        const chord = chordFromType(s.root, lilyQuality(s.quality), toUnits(s.beatsQ));
        // chordFromType floors a chord at one beat for hand entry; the book
        // writes eighth-note anticipations (Misty's E7♯9 on the "and" of 4,
        // Billie's Bounce's pickup C7), which floored overfilled the bar and
        // pushed every later chord past its onset. Keep the written span,
        // as the MusicXML importer does.
        chord.beats = toUnits(s.beatsQ);
        // The book's own symbol wins for display (Em, F, A13, F+, B7alt,
        // G7/D…); the catalogue chordType stays for playback/voicing.
        chord.quality = lilyDisplayQuality(s.quality);
        if (s.bass) chord.bass = s.bass;
        chord.symbol = chartChordSymbol(chord);
        harmony.push(chord);
      } else if (harmony.length > 0 && !s.rest) {
        harmony[harmony.length - 1].beats += toUnits(s.beatsQ);
      } else if (harmony.length > 0 && harmony[harmony.length - 1].noChord) {
        harmony[harmony.length - 1].beats += toUnits(s.beatsQ);
      } else if (s.rest && harmony.length > 0) {
        // A chordmode rest mid-bar is a real break (`c2:7 r` — the band
        // stops under the pickup lick), not a continuation of the chord.
        harmony.push({
          id: uid('c'),
          root: '',
          quality: '',
          chordType: null,
          beats: toUnits(s.beatsQ),
          noChord: true,
          symbol: 'N.C.',
        });
      } else {
        // leading spacer: silence until the first chord
        harmony.push({
          id: uid('c'),
          root: '',
          quality: '',
          chordType: null,
          beats: toUnits(s.beatsQ),
          noChord: true,
          symbol: 'N.C.',
        });
      }
    }
    const fm: FormMeasure = { id: uid('obm'), harmony };
    if (pickup) fm.pickupBeats = (pickupQ! * meter[1]) / 4;
    if (sig) fm.timeSig = sig;
    else if (measures.length > 0) {
      // restore the meter after an irregular bar (never re-stamp otherwise)
      const prev = measures[measures.length - 1].timeSig;
      if (prev && (prev[0] !== meter[0] || prev[1] !== meter[1])) fm.timeSig = meter;
    }
    if (measures.length === 0 && !fm.timeSig) fm.timeSig = meter;
    if (pending.mark) fm.section = pending.mark;
    if (pending.mark === 'Coda') fm.coda = true;
    if (pending.segno) fm.segno = true;
    if (pending.coda) fm.coda = true;
    if (pending.directive) fm.directive = pending.directive;
    if (pending.openRepeat) fm.open = 'repeat';
    if (pending.ending != null) fm.ending = pending.ending;
    pending = {};
    measures.push(fm);
    symbols = [];
    barQ = 0;
  };

  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    if (
      t === '\\startChords' ||
      t === '\\endChords' ||
      t === '\\startSong' ||
      t === '\\endSong' ||
      t === '\\startPart' ||
      t === '\\endPart' ||
      t === '\\LPC' ||
      t === '\\RPC' ||
      t === '\\myEndLine' ||
      t === '\\myEndLineVoltaNotLast' ||
      t === '\\myEndLineVoltaLast' ||
      t === '\\chordmode'
    ) {
      continue;
    }
    if (t === '\\myMark') {
      pending.mark = stripQuotes(tokens[++i]);
      continue;
    }
    if (t === '\\mark') {
      if (tokens[i + 1]?.startsWith('"')) pending.directive = stripQuotes(tokens[++i]);
      continue;
    }
    if (t === '\\mySegno') {
      pending.segno = true;
      continue;
    }
    if (t === '\\myCoda') {
      pending.coda = true;
      continue;
    }
    if (t === '\\partial') {
      // Keep the tune's real meter and record the pickup's actual sounding
      // length separately. Padding it to a full bar shifts every repeat and
      // downbeat that follows.
      pickupQ = quartersOfDurToken(tokens[++i]);
      continue;
    }
    if (t === '\\repeat') {
      i += 2; // "volta" N
      pendingRepeat = true;
      continue;
    }
    if (t === '\\alternative') {
      pendingAlt = true;
      continue;
    }
    if (t === '{') {
      if (pendingRepeat) {
        depth.push('repeat');
        pending.openRepeat = true;
        pendingRepeat = false;
      } else if (pendingAlt) {
        depth.push('alt');
        altIndex = 0;
        pendingAlt = false;
      } else if (depth[depth.length - 1] === 'alt') {
        depth.push('altGroup');
        pending.ending = ++altIndex;
      } else {
        depth.push('plain');
      }
      continue;
    }
    if (t === '}') {
      if (barQ > 0) pushMeasure();
      const k = depth.pop();
      if (k === 'repeat' && tokens[i + 1] !== '\\alternative') {
        if (measures.length > 0) measures[measures.length - 1].close = 'repeat';
      } else if (k === 'altGroup' && altIndex === 1) {
        if (measures.length > 0) measures[measures.length - 1].close = 'repeat';
      }
      continue;
    }
    if (t === '|') {
      if (barQ > 0) pushMeasure();
      continue;
    }
    const parsed = parseChordToken(t);
    if (!parsed) continue;
    let durQ = parsed.durQ ?? lastDurQ;
    if (parsed.mult != null) durQ *= parsed.mult;
    if (parsed.durQ != null || parsed.mult != null) lastDurQ = durQ;
    // Split a bar-spanning symbol at each barline.
    let remaining = durQ;
    while (remaining > 1e-9) {
      let isPickupBar = measures.length === 0 && pickupQ != null;
      let barCapacity = isPickupBar ? pickupQ! : quartersPerBar;
      // A bar that filled exactly stays open for its `|` — a mark written
      // after its last chord (500 Miles High's D.S. al Coda) still belongs
      // to it. But bar checks are optional: a chord arriving at a full bar
      // opens the next one (Prelude To A Kiss's `c2 d:7 d:m7 g:7.9- |`),
      // where it used to go in as a zero-length phantom the 1-beat floor
      // then turned into a fifth beat, struck twice.
      if (barQ > 1e-9 && barQ >= barCapacity - 1e-6) {
        pushMeasure();
        isPickupBar = measures.length === 0 && pickupQ != null;
        barCapacity = isPickupBar ? pickupQ! : quartersPerBar;
      }
      const take = Math.min(remaining, barCapacity - barQ);
      symbols.push({ root: parsed.root, quality: parsed.quality, bass: parsed.bass, beatsQ: take, ...(parsed.rest ? { rest: true } : {}) });
      barQ += take;
      remaining -= take;
      // A pickup often has no explicit barline between its spacer and the
      // first body chord. Close it as soon as its declared duration is full,
      // otherwise the body chord leaks into the pickup and shifts repeats.
      if (barQ >= barCapacity - 1e-6 && (isPickupBar || remaining > 1e-9)) pushMeasure();
    }
  }
  if (barQ > 0) pushMeasure();

  return measures;
}

function stripQuotes(s: string): string {
  return s?.replace(/^"|"$/g, '') ?? '';
}

function quartersOfDurToken(tok: string): number {
  const m = tok.match(/^(\d+)(\.*)(?:\*(\d+))?$/);
  if (!m) return 1;
  let q = 4 / Number(m[1]);
  let dot = q / 2;
  for (let i = 0; i < m[2].length; i++) {
    q += dot;
    dot /= 2;
  }
  return q * (m[3] ? Number(m[3]) : 1);
}

function parseChordToken(tok: string): {
  root: string | null;
  quality: string;
  bass: string | null;
  durQ: number | null;
  mult: number | null;
  /** A chordmode REST (`r`): a true N.C. span, unlike the `s` spacer. */
  rest?: boolean;
} | null {
  // The quality may be EMPTY before a slash bass (`g2:/b` = G triad over B
  // — Lost In The Stars); requiring 1+ chars dropped the whole chord and
  // the duration latch drifted an extra bar into the form.
  // A chordmode TIE (`ees1~ | ees2 …` — Among My Souvenirs) just sustains
  // the symbol across the barline; the chart restates the chord, so the
  // tilde is display-only — but left on the token it failed the match and
  // dropped the chord, collapsing its bar.
  const m = tok.replace(/~$/, '').match(
    /^([a-g](?:is|es|s)*|s|r)[',]*(\d+\.*)?(?:\*(\d+))?(?::([^/]*))?(?:\/\+?([a-g](?:is|es|s)*)[',]*)?$/,
  );
  if (!m) return null;
  const [, rootTok, durTok, multTok, quality, bassTok] = m;
  if (rootTok !== 's' && rootTok !== 'r' && !NOTE_LETTERS.has(rootTok[0])) return null;
  return {
    root: rootTok === 's' || rootTok === 'r' ? null : lilyRootName(rootTok),
    ...(rootTok === 'r' ? { rest: true } : {}),
    quality: quality ?? '',
    bass: bassTok ? lilyRootName(bassTok) : null,
    durQ: durTok ? quartersOfDurToken(durTok) : null,
    mult: multTok ? Number(multTok) : null,
  };
}

function tokenize(src: string): string[] {
  const out: string[] = [];
  const re = /"[^"]*"|\\[A-Za-z]+|\{|\}|\||[^\s{}|]+/g;
  for (let m = re.exec(src); m; m = re.exec(src)) out.push(m[0]);
  return out;
}

// ------------------------------------------------------------------- voice

export interface VoiceHeader {
  tempoText?: string;
  bpm?: number;
  meter: [number, number];
  key: KeySignature;
  partialQ: number;
}

const LETTERS = ['c', 'd', 'e', 'f', 'g', 'a', 'b'] as const;

interface RelNote {
  letter: (typeof LETTERS)[number];
  alter: number;
  octave: number; // scientific pitch octave
}

/** LilyPond relative motion: nearest letter (≤3 steps), then '/, shifts. */
function resolveRelative(
  prev: RelNote,
  letter: RelNote['letter'],
  alter: number,
  marks: number,
): RelNote {
  const prevIdx = LETTERS.indexOf(prev.letter) + prev.octave * 7;
  let best = prev.octave;
  let bestDist = Infinity;
  for (const oct of [prev.octave - 1, prev.octave, prev.octave + 1]) {
    const idx = LETTERS.indexOf(letter) + oct * 7;
    const dist = Math.abs(idx - prevIdx);
    if (dist < bestDist || (dist === bestDist && idx < prevIdx)) {
      bestDist = dist;
      best = oct;
    }
  }
  return { letter, alter, octave: best + marks };
}

interface ChordMember {
  letter: RelNote['letter'];
  alter: number;
  marks: number;
}

type StreamTok =
  | { kind: 'note'; members: ChordMember[]; durQ: number; tie: boolean; fermata?: boolean; nav?: VoiceNav; grace?: boolean }
  | { kind: 'rest'; durQ: number; fermata?: boolean; nav?: VoiceNav }
  | { kind: 'bar' }
  | { kind: 'tupletStart'; num: number; den: number }
  | { kind: 'tupletEnd' }
  | { kind: 'repeatStart'; unfold: boolean; count: number }
  | { kind: 'repeatEnd' }
  | { kind: 'altStart' }
  | { kind: 'altSep' }
  | { kind: 'altEnd' };

/** Navigation attached to a melody note: To Coda marks its bar (or the next,
 *  for a mid-bar sign); the rest land as the bar's directive. */
export type VoiceNav = 'toCoda' | 'D.S. al Coda' | 'D.C. al Coda' | 'D.C. al Fine' | 'Fine';

type ResolvedTok =
  | { kind: 'note'; pitches: SpelledPitch[]; durQ: number; tie: boolean; fermata?: boolean; nav?: VoiceNav; grace?: boolean }
  | Exclude<StreamTok, { kind: 'note' }>;

export interface ParsedVoice {
  header: VoiceHeader;
  /** WRITTEN-order items (repeat body once, then each volta's bars). */
  items: VoiceItem[];
  /** Items whose note carried a `\fermata` — stamped onto their bar's form measure. */
  fermatas: Set<VoiceItem>;
  /** Items whose note carried a navigation markup — stamped at distribution. */
  navs: Map<VoiceItem, VoiceNav>;
}

/** Parse a melody block in the openbook subset (always `\relative c'`). */
export function parseVoice(block: string): ParsedVoice {
  let src = block.replace(/%%?[^\n]*/g, '');
  // Navigation rides melody notes as markup (`fes^\\markup {To Coda …}`,
  // `bes'^\\markup {D.C. al Coda}`, `r2^\\markup { \\italic { Fine } }`) —
  // the openbook house style for signs the chords lane doesn't carry. Convert
  // each to a sentinel token BEFORE the generic markup strip eats it; the
  // token loop marks the note it followed and distribution stamps its bar.
  src = src
    .replace(/[_^-]?\\markup\s*\{\s*To Coda[^}]*\}/g, ' \\navToCoda ')
    .replace(/[_^-]?\\markup\s*\{\s*D\.S\. al Coda\s*\}/g, ' \\navDSalCoda ')
    .replace(/[_^-]?\\markup\s*\{\s*D\.C\. al Coda\s*\}/g, ' \\navDCalCoda ')
    .replace(/[_^-]?\\markup\s*\{\s*\\italic\s*\{\s*D\.C\. al Fine\s*\}\s*\}/g, ' \\navDCalFine ')
    .replace(/[_^-]?\\markup\s*\{\s*\\italic\s*\{\s*Fine\s*\}\s*\}/g, ' \\navFine ')
    .replace(/[_^-]?\\markup\s*\{\s*\\italic\s*\{\s*D\.S\. al Coda\s*\}\s*\}/g, ' \\navDSalCoda ');
  src = src.replace(/[_^-]?\\markup\s*\{[^{}]*(\{[^}]*\}[^{}]*)*\}/g, '');
  // Attached dynamics and hairpins ride note tokens with no space
  // (`ees'1\p\<~`, `ees4\!` — Watermelon Man) — strip them before
  // tokenizing or the note itself fails to parse and drops. The lookahead
  // keeps `\parenthesize` alive past the `\p` rule.
  src = src.replace(
    /\\(?:pp?p?|ff?f?|m[pf]|sfz?|rfz|fp|espressivo|breathe|repeatTie)(?![a-zA-Z])/g,
    '',
  );
  src = src.replace(/\\[<>!]/g, '');
  // A fermata rides its note (`c1\\fermata`): split it into its own token so
  // the loop can mark the note it follows — the hold lands on the chart as a
  // measure-level fermata rather than being silently dropped.
  src = src.replace(/\\fermata(?![a-zA-Z])/g, ' \\fermata ');
  // Text attachments (`d4^"(Spoken)"` — Satin Doll) are display-only.
  src = src.replace(/[_^]"[^"]*"/g, '');
  // A tie glued straight onto the next note (`bes8~bes4` — Billie's
  // Bounce) is two tokens.
  src = src.replace(/~(?=[a-gR])/g, '~ ');
  // Manual beam brackets attach to tokens (`bes'16[ c8. bes16]`).
  src = src.replace(/[[\]]/g, ' ');
  src = src.replace(/\\parenthesize\s*/g, '');
  // A melody CHORD `<aes c>4-.` collapses to ONE @-joined token
  // (`aes@c4-.`) so the whitespace tokenizer keeps it whole; the note loop
  // resolves every member (multi-notehead events, LilyPond's relative
  // rules). Hairpin `\<`/`\>` were stripped above, so every remaining
  // angle pair is a chord.
  src = src.replace(/<([^<>]+)>/g, (_, inner: string) => inner.trim().split(/\s+/).join('@'));
  const header: VoiceHeader = { meter: [4, 4], key: { tonic: 'C', mode: 'major' }, partialQ: 0 };
  const tempoM = src.match(/\\tempo\s+"([^"]+)"(?:\s+(\d+)\s*=\s*(\d+))?/);
  if (tempoM) {
    header.tempoText = tempoM[1];
    if (tempoM[3]) header.bpm = Math.round(Number(tempoM[3]) / (Number(tempoM[2]) / 4));
  }
  const timeM = src.match(/\\time\s+(\d+)\/(\d+)/);
  if (timeM) header.meter = [Number(timeM[1]), Number(timeM[2])];
  const keyM = src.match(/\\key\s+([a-g](?:is|es)?)\s*\\(major|minor)/);
  if (keyM) header.key = { tonic: lilyRootName(keyM[1]), mode: keyM[2] as 'major' | 'minor' };
  const partialM = src.match(/\\partial\s+(\d+\.*)/);
  if (partialM) header.partialQ = quartersOfDurToken(partialM[1]);

  src = src
    .replace(/\\tempo\s+"[^"]*"(\s+\d+\s*=\s*\d+)?/, '')
    .replace(/\\time\s+\d+\/\d+/, '')
    .replace(/\\key\s+[a-g](?:is|es)?\s*\\(major|minor)/g, '')
    .replace(/\\partial\s+\d+\.*/, '');

  const tokens = tokenize(src);
  if (tokens[0] === '{') tokens.shift();
  if (tokens[tokens.length - 1] === '}') tokens.pop();

  const stream: StreamTok[] = [];
  let lastDurQ = 1;
  let lastMembers: ChordMember[] | null = null;
  let pendingGrace = false;
  let i = 0;
  const depth: ('plain' | 'tuplet' | 'repeat' | 'alt' | 'altGroup')[] = [];
  let pendingTuplet: { num: number; den: number } | null = null;
  let pendingRepeat: { unfold: boolean; count: number } | null = null;
  let pendingAlt = false;
  while (i < tokens.length) {
    const t = tokens[i++];
    if (t === '\\tuplet') {
      const fm = tokens[i++]?.match(/^(\d+)\/(\d+)$/);
      pendingTuplet = fm ? { num: Number(fm[1]), den: Number(fm[2]) } : { num: 3, den: 2 };
      continue;
    }
    if (t === '\\repeat') {
      // "volta N" keeps written order (the form carries the repeat marks);
      // "unfold N" EXPANDS in notation, so it must expand here too.
      const type = tokens[i] ?? 'volta';
      const count = Number(tokens[i + 1]) || 2;
      i += 2;
      pendingRepeat = { unfold: type === 'unfold', count };
      continue;
    }
    if (t === '\\alternative') {
      pendingAlt = true;
      continue;
    }
    if (t === '{') {
      if (pendingTuplet) {
        stream.push({ kind: 'tupletStart', ...pendingTuplet });
        depth.push('tuplet');
        pendingTuplet = null;
      } else if (pendingRepeat) {
        stream.push({ kind: 'repeatStart', ...pendingRepeat });
        depth.push('repeat');
        pendingRepeat = null;
      } else if (pendingAlt) {
        depth.push('alt');
        stream.push({ kind: 'altStart' });
        pendingAlt = false;
      } else if (depth[depth.length - 1] === 'alt') {
        depth.push('altGroup');
      } else {
        depth.push('plain');
      }
      continue;
    }
    if (t === '}') {
      const k = depth.pop();
      if (k === 'tuplet') stream.push({ kind: 'tupletEnd' });
      else if (k === 'repeat') stream.push({ kind: 'repeatEnd' });
      else if (k === 'altGroup') stream.push({ kind: 'altSep' });
      else if (k === 'alt') stream.push({ kind: 'altEnd' });
      continue;
    }
    if (t === '|') {
      stream.push({ kind: 'bar' });
      continue;
    }
    if (t === '\\fermata') {
      const last = stream[stream.length - 1];
      if (last && (last.kind === 'note' || last.kind === 'rest')) last.fermata = true;
      continue;
    }
    if (t === '\\grace') {
      // A single-note grace (`\\grace a8 bes~` — Night In Tunisia): the next
      // note sounds as an ornament with zero musical time. Skipping the
      // command but keeping the note used to add a phantom eighth.
      pendingGrace = true;
      continue;
    }
    if (t.startsWith('\\nav')) {
      const nav: VoiceNav | null =
        t === '\\navToCoda' ? 'toCoda'
        : t === '\\navDSalCoda' ? 'D.S. al Coda'
        : t === '\\navDCalCoda' ? 'D.C. al Coda'
        : t === '\\navDCalFine' ? 'D.C. al Fine'
        : t === '\\navFine' ? 'Fine'
        : null;
      const last = stream[stream.length - 1];
      if (nav && last && (last.kind === 'note' || last.kind === 'rest')) last.nav = nav;
      continue;
    }
    if (t.startsWith('\\') || t === '(' || t === ')') continue;
    // A BARE DURATION after a note is a detached-duration TYPO (`fis 8` in
    // Greensleeves — the parallel A-section bar writes `fis8` attached, and
    // the corpus targets a LilyPond too old for intentional isolated
    // durations): RETIME the note it follows rather than sounding a second
    // one, which overfilled the bar and leaked melody past the form's end.
    const bare = t.match(/^(\d+\.*)(\*\d+)?(~?)$/);
    if (bare && lastMembers) {
      let durQ = quartersOfDurToken(bare[1]);
      if (bare[2]) durQ *= Number(bare[2].slice(1));
      lastDurQ = durQ;
      const prev = stream[stream.length - 1];
      if (prev && prev.kind === 'note') {
        prev.durQ = durQ;
        if (bare[3] === '~') prev.tie = true;
        continue;
      }
      stream.push({
        kind: 'note',
        members: lastMembers.map((m) => ({ ...m, marks: 0 })),
        durQ,
        tie: bare[3] === '~',
      });
      continue;
    }
    // `[!?]` is LilyPond's forced/cautionary accidental and comes AFTER the
    // octave marks (`ees'!8` — accepting it only before the marks dropped
    // that note, and the duration latch then derailed the whole line). The
    // accidental group takes `is`/`es` runs AND the Dutch short flats
    // (`as` = aes, `es` = ees — Billie's Bounce writes `as'8`). The
    // trailing `-…` group covers FINGERINGS (Donna Lee carries Ofer
    // Portugali's: `bes-4 a-3 c,-1 ees8-2`) and ARTICULATIONS (`ges8-.`,
    // `ges->` — The Man I Love) — accepted and dropped, or the marked note
    // itself silently vanishes from the melody.
    // A chord token is @-joined (`aes@c4-.`): only the LAST member carries
    // the duration/tie/suffix; leading members are bare pitches.
    const ACC = '((?:is)+|(?:es)+|s(?:es)*)?';
    const parts = t.split('@');
    const nm = parts[parts.length - 1].match(
      // `R` is the WHOLE-BAR (multi-measure) rest: `R1*4` spans four bars
      // and the bar distribution splits it (Like Someone In Love's volta).
      // parens may land either side of the tie (`a8)~` — Billie's Bounce)
      new RegExp(
        `^([a-grsR])${ACC}('+|,+)?[!?]?(\\d+\\.*)?(\\*\\d+)?(?:-(?:\\d+|[.>^_+-]))*[()]*(~?)[()]*$`,
      ),
    );
    if (!nm) continue;
    const alterOf = (acc: string | undefined): number => {
      if (!acc) return 0;
      if (acc.startsWith('is')) return acc.length / 2;
      return acc === 's' ? -1 : -Math.ceil(acc.length / 2);
    };
    const memberOf = (p: string): ChordMember | null => {
      const mm = p.match(new RegExp(`^([a-g])${ACC}('+|,+)?[!?]?$`));
      if (!mm) return null;
      return {
        letter: mm[1] as RelNote['letter'],
        alter: alterOf(mm[2]),
        marks: mm[3] ? (mm[3][0] === "'" ? mm[3].length : -mm[3].length) : 0,
      };
    };
    const leading = parts.slice(0, -1).map(memberOf);
    if (leading.some((m) => m === null)) continue;
    const [, letterTok, accTok, marksTok, durTok, multTok, tieTok] = nm;
    let durQ = durTok ? quartersOfDurToken(durTok) : lastDurQ;
    if (multTok) durQ *= Number(multTok.slice(1));
    if (durTok || multTok) lastDurQ = durQ;
    if (letterTok === 'r' || letterTok === 's' || letterTok === 'R') {
      stream.push({ kind: 'rest', durQ });
      continue;
    }
    const marks = marksTok ? (marksTok[0] === "'" ? marksTok.length : -marksTok.length) : 0;
    const members = [
      ...(leading as ChordMember[]),
      { letter: letterTok as RelNote['letter'], alter: alterOf(accTok), marks },
    ];
    lastMembers = members;
    stream.push({ kind: 'note', members, durQ, tie: tieTok === '~', ...(pendingGrace ? { grace: true } : {}) });
    pendingGrace = false;
  }

  // Resolve pitches on the WRITTEN stream (LilyPond's relative walk follows
  // written order; repeats expand afterwards with resolved pitches). Chord
  // rule: the FIRST member resolves against the previous event and is the
  // reference the NEXT event resolves from; inner members resolve
  // member-to-member.
  let prev: RelNote = { letter: 'c', alter: 0, octave: 4 };
  const resolved: ResolvedTok[] = stream.map((tk) => {
    if (tk.kind !== 'note') return tk;
    let ref = prev;
    const pitches: SpelledPitch[] = tk.members.map((m, k) => {
      const rel = resolveRelative(ref, m.letter, m.alter, m.marks);
      ref = rel;
      if (k === 0) prev = rel;
      return { step: rel.letter.toUpperCase() as Step, alter: rel.alter, octave: rel.octave };
    });
    return { kind: 'note', pitches, durQ: tk.durQ, tie: tk.tie, ...(tk.fermata ? { fermata: true } : {}), ...(tk.nav ? { nav: tk.nav } : {}), ...(tk.grace ? { grace: true } : {}) };
  });

  // \repeat UNFOLD expands in notation (LilyPond writes the body out), so it
  // must expand here too — the chord side of these tunes is written out in
  // full. LilyPond resolves \relative on the WRITTEN body once, so copying
  // resolved tokens is exact. Alternatives cycle per pass; with fewer
  // alternatives than passes the first alternative repeats (LilyPond rule).
  const expandUnfolds = (toks: ResolvedTok[]): ResolvedTok[] => {
    const out: ResolvedTok[] = [];
    let k = 0;
    while (k < toks.length) {
      const tk = toks[k];
      if (tk.kind !== 'repeatStart' || !tk.unfold) {
        out.push(tk);
        k++;
        continue;
      }
      k++;
      const body: ResolvedTok[] = [];
      let d = 0;
      while (k < toks.length && !(d === 0 && toks[k].kind === 'repeatEnd')) {
        if (toks[k].kind === 'repeatStart') d++;
        if (toks[k].kind === 'repeatEnd') d--;
        body.push(toks[k]);
        k++;
      }
      k++; // consume repeatEnd
      const alts: ResolvedTok[][] = [];
      if (toks[k]?.kind === 'altStart') {
        k++;
        let cur: ResolvedTok[] = [];
        let dd = 0;
        while (k < toks.length) {
          const a = toks[k];
          k++;
          if (a.kind === 'altSep' && dd === 0) {
            alts.push(cur);
            cur = [];
          } else if (a.kind === 'altEnd' && dd === 0) {
            if (cur.length > 0) alts.push(cur);
            break;
          } else {
            if (a.kind === 'repeatStart') dd++;
            if (a.kind === 'repeatEnd') dd--;
            cur.push(a);
          }
        }
      }
      const bodyX = expandUnfolds(body);
      for (let pass = 0; pass < tk.count; pass++) {
        out.push(...bodyX);
        if (alts.length > 0) out.push(...alts[Math.max(0, pass - (tk.count - alts.length))]);
      }
    }
    return out;
  };

  // KEEP WRITTEN ORDER for volta repeats: the form carries the repeat/volta
  // structure (open/close/ending marks), so the melody must stay written —
  // body once, then each alternative's bars — exactly the source token
  // order. (Expanding voltas to performance order here would put a 70-bar
  // melody against a 38-bar form; flattenChart expands both downstream.)
  const written = expandUnfolds(resolved).filter(
    (tk) =>
      tk.kind === 'note' ||
      tk.kind === 'rest' ||
      tk.kind === 'tupletStart' ||
      tk.kind === 'tupletEnd',
  );

  const items: VoiceItem[] = [];
  const fermatas = new Set<VoiceItem>();
  const navs = new Map<VoiceItem, VoiceNav>();
  let tup: { num: number; den: number; items: NoteEvent[] } | null = null;
  for (const tk of written) {
    if (tk.kind === 'tupletStart') {
      tup = { num: tk.num, den: tk.den, items: [] };
      continue;
    }
    if (tk.kind === 'tupletEnd') {
      if (tup) {
        const tg: TupletGroup = {
          id: uid('obt'),
          kind: 'tuplet',
          actual: tup.num,
          normal: tup.den,
          items: tup.items,
        };
        // Distribution sees the group as one item, so a held member marks it.
        if (tup.items.some((e) => fermatas.has(e))) fermatas.add(tg);
        const memberNav = tup.items.map((e) => navs.get(e)).find(Boolean);
        if (memberNav) navs.set(tg, memberNav);
        items.push(tg);
        tup = null;
      }
      continue;
    }
    if (tk.kind !== 'note' && tk.kind !== 'rest') continue;
    const ev = eventOf(
      tk.kind === 'rest',
      tk.kind === 'note' ? tk.pitches : [],
      tk.durQ,
      tk.kind === 'note' && tk.tie,
    );
    if (tk.kind === 'note' && tk.grace) ev.grace = true;
    if ((tk.kind === 'note' || tk.kind === 'rest') && tk.fermata) fermatas.add(ev);
    if ((tk.kind === 'note' || tk.kind === 'rest') && tk.nav) navs.set(ev, tk.nav);
    if (tup) tup.items.push(ev);
    else items.push(ev);
  }
  return { header, items, fermatas, navs };
}

function eventOf(
  rest: boolean,
  pitches: SpelledPitch[],
  durQ: number,
  tieStart?: boolean,
): NoteEvent {
  return {
    id: uid('obn'),
    kind: rest ? 'rest' : 'note',
    duration: durationOfQuarters(durQ),
    notes: rest
      ? []
      : pitches.map((pitch) => ({ pitch, ...(tieStart ? { tie: 'start' as const } : {}) })),
  };
}

/**
 * An arbitrary span as a run of representable events. A 2.5-quarter piece
 * (a pickup pad, or a barline split's head/tail) has no single written
 * value — one event would silently round DOWN (2.5 → a plain half) and the
 * lost time derails every later bar. Notes tie the pieces together; the
 * final piece carries the span's own outgoing tie.
 */
function eventsOf(
  rest: boolean,
  pitches: SpelledPitch[],
  durQ: number,
  tieStart?: boolean,
): NoteEvent[] {
  const out: NoteEvent[] = [];
  let left = durQ;
  const values: number[] = [];
  for (const base of NOTE_VALUES) {
    for (const dots of [0, 1, 2]) {
      let v = quartersOf(base);
      let d = v / 2;
      for (let k = 0; k < dots; k++) {
        v += d;
        d /= 2;
      }
      values.push(v);
    }
  }
  values.sort((a, b) => b - a);
  while (left > 1e-6) {
    const v = values.find((x) => x <= left + 1e-9) ?? left;
    const last = left - v <= 1e-6;
    out.push(eventOf(rest, pitches, v, last ? tieStart : !rest));
    left -= v;
  }
  return out;
}

function durationOfQuarters(q: number): Duration {
  for (const base of NOTE_VALUES) {
    for (const dots of [0, 1, 2] as const) {
      const d: Duration = dots ? { base, dots } : { base };
      if (equalsF(durationQuarters(d), frac(Math.round(q * 64), 64))) return d;
    }
  }
  // nearest plain value below (tuplet members pass through their own path)
  let best: NoteValue = 'quarter';
  let bestQ = 0;
  for (const base of NOTE_VALUES) {
    const bq = quartersOf(base);
    if (bq <= q + 1e-9 && bq > bestQ) {
      bestQ = bq;
      best = base;
    }
  }
  return { base: best };
}

function quartersOf(base: NoteValue): number {
  const map: Record<string, number> = {
    breve: 8,
    whole: 4,
    half: 2,
    quarter: 1,
    eighth: 0.5,
    '16th': 0.25,
    '32nd': 0.125,
    '64th': 0.0625,
  };
  return map[base] ?? 1;
}

function eventQuarters(ev: NoteEvent): number {
  if (ev.grace) return 0;
  let q = quartersOf(ev.duration.base);
  let d = q / 2;
  for (let k = 0; k < (ev.duration.dots ?? 0); k++) {
    q += d;
    d /= 2;
  }
  return q;
}

function itemQuarters(it: VoiceItem): number {
  if (it.kind === 'tuplet') {
    return (it.items.reduce((s, ev) => s + itemQuarters(ev), 0) * it.normal) / it.actual;
  }
  return eventQuarters(it);
}

// ------------------------------------------------------------------ lyrics

/** Verse syllables from a \lyricmode block. `skip` = the `_` placeholder. */
export function parseLyrics(block: string): { text: string; hyphen?: boolean; skip?: boolean }[] {
  const src = block
    .replace(/\\lyricmode\s*\{/, '')
    .replace(/\}\s*$/, '')
    .replace(/%%?[^\n]*/g, '')
    // Styled syllables (`\markup \italic Switch` — Satin Doll's spoken
    // line) keep the WORD; the commands and braces are presentation.
    .replace(/\\[a-zA-Z]+/g, '')
    .replace(/[{}]/g, ' ');
  // A quoted syllable may escape a quote the lyric prints (`"\"Have"`,
  // `"Jones?\""` — 21 verses in the corpus): read as a plain string, the
  // escape closed the syllable early and the stray quote left over opened
  // one that swallowed whole lines, shifting every later syllable.
  const raw = src.match(/"(?:\\.|[^"\\])*"|--|__|[^\s]+/g) ?? [];
  const out: { text: string; hyphen?: boolean; skip?: boolean }[] = [];
  for (const t of raw) {
    if (t === '--') {
      if (out.length > 0) out[out.length - 1].hyphen = true;
      continue;
    }
    if (t === '__') continue;
    if (t === '_') {
      out.push({ text: '', skip: true });
      continue;
    }
    let word = stripQuotes(t);
    if (t.startsWith('"')) word = word.replace(/\\(["\\])/g, '$1');
    // An underscore INSIDE a word is lyricmode's escaped space
    // (`my_arms` sings "my arms" — Caravan); a lone `_` was the skip above.
    out.push({ text: word.replace(/_/g, ' ') });
  }
  return out;
}

// ------------------------------------------------------------------- score

export interface OpenbookTune {
  title: string;
  composer?: string;
  style?: string;
  piece?: string;
  version: string;
  score: Score;
  /**
   * Melody quarters left UNDISTRIBUTED after every form bar filled — always 0
   * for a healthy tune. A non-zero value means the voice and the chord lane
   * disagree on the form's length (a dropped token, a phantom note), the bug
   * class that silently drifted Body And Soul's melody a beat per key change.
   */
  melodyLeftoverQ: number;
}

const flatEvents = (items: VoiceItem[]): NoteEvent[] =>
  items.flatMap((it) => (it.kind === 'tuplet' ? flatEvents(it.items) : [it]));

/** LilyPond writes only the outgoing `~`; the score model needs both ends. */
function completeIncomingTies(measures: Voice[][]): void {
  let pending = new Set<number>();
  for (const voices of measures) {
    for (const event of flatEvents(voices[0]?.items ?? [])) {
      if (event.grace) continue;
      for (const head of event.notes) {
        if (pending.has(midiOf(head.pitch))) {
          head.tie = head.tie === 'start' || head.tie === 'both' ? 'both' : 'stop';
        }
      }
      // A rest or a different pitch ends the chain; never consume a later
      // repeated pitch just because an earlier note had a dangling tilde.
      pending = new Set(
        event.notes
          .filter((head) => head.tie === 'start' || head.tie === 'both')
          .map((head) => midiOf(head.pitch)),
      );
    }
  }
}

/** Parse one .ly.mako tune (its default version) into a Score. */
export function parseOpenbook(src: string): OpenbookTune {
  const attrs = makoAttributes(src);
  const { def } = makoVersions(src);
  const chordsBlock = makoSection(src, `Chords${def}`);
  const voiceBlock = makoSection(src, `Voice${def}`);
  if (!voiceBlock) throw new Error(`openbook: no Voice${def} section`);

  const voice = parseVoice(voiceBlock);
  // Half-note meters re-express in quarters (2/2 → 4/4, 3/2 → 6/4): the
  // chart model's harmony beats are INTEGER denominator units (chordFromType
  // clamps to ≥1), and Misty's coda writes four QUARTER chords in a 2/2 bar
  // — half-unit durations clamped to 1 and spilled past the barline.
  const meter: [number, number] =
    voice.header.meter[1] === 2 ? [voice.header.meter[0] * 2, 4] : voice.header.meter;
  const measures = chordsBlock ? parseChords(chordsBlock, meter) : [];

  const meterQ = (meter[0] * 4) / meter[1];
  const chordsPartial = chordsBlock?.match(/\\partial\s+(\d+\.*(?:\*\d+)?)/);
  const partialQ =
    voice.header.partialQ > 1e-9
      ? voice.header.partialQ
      : chordsPartial
        ? quartersOfDurToken(chordsPartial[1])
        : 0;

  // Some OpenBook files declare \partial only in the voice. If the chord
  // lane did not create a short first measure, insert an aligned N.C. pickup
  // instead of attaching the pickup duration to the first full body bar.
  if (partialQ > 1e-9 && partialQ < meterQ - 1e-9 && measures.length > 0) {
    const pickupBeats = (partialQ * meter[1]) / 4;
    if (measures[0].pickupBeats == null) {
      const firstHarmonyBeats = measures[0].harmony.reduce((sum, chord) => sum + chord.beats, 0);
      if (firstHarmonyBeats <= pickupBeats + 1e-6) {
        measures[0].pickupBeats = pickupBeats;
      } else {
        measures[0].timeSig = undefined;
        measures.unshift({
          id: uid('obm'),
          timeSig: meter,
          pickupBeats,
          harmony: [
            {
              id: uid('c'),
              root: '',
              quality: '',
              chordType: null,
              beats: pickupBeats,
              noChord: true,
              symbol: 'N.C.',
            },
          ],
        });
      }
    }
  }

  // Bar lengths in quarters. A pickup keeps the real meter but consumes only
  // pickupBeats, so the next authored bar starts on its actual downbeat.
  let runningMeter = meter;
  const barsQ = measures.map((m) => {
    runningMeter = m.timeSig ?? runningMeter;
    return ((m.pickupBeats ?? runningMeter[0]) * 4) / runningMeter[1];
  });
  if (barsQ.length === 0) {
    const totalQ = voice.items.reduce((s, it) => s + itemQuarters(it), 0);
    const per = (meter[0] * 4) / meter[1];
    for (let q = 0; q < totalQ - 1e-6; q += per) {
      measures.push({
        id: uid('obm'),
        harmony: [],
        ...(measures.length === 0 ? { timeSig: meter } : {}),
      });
      barsQ.push(per);
    }
  }

  // Distribute melody items across the bars; a note crossing a barline
  // splits into a tied pair (the engraver's expectation). LilyPond's
  // \partial sets score-level timing, so a partial written ONLY in the
  // chords block still governs the voice (I Left My Heart's voice opens with
  // a bare 3-quarter bar; the chords carry `\partial 2.`).
  const queue: VoiceItem[] = [...voice.items];
  const lyricsVerses = collectLyrics(src, def);
  assignLyrics(queue, lyricsVerses);
  const partMeasures: Voice[][] = [];
  let qi = 0;
  for (let b = 0; b < measures.length; b++) {
    const budget = barsQ[b];
    const items: VoiceItem[] = [];
    let used = 0;
    while (used + 1e-9 < budget && qi < queue.length) {
      const it = queue[qi];
      const q = itemQuarters(it);
      if (used + q > budget + 1e-6 && it.kind !== 'tuplet') {
        const headQ = budget - used;
        const tailQ = q - headQ;
        const pitches = it.notes.map((n) => n.pitch);
        const head = eventsOf(it.kind === 'rest', pitches, headQ, it.kind === 'note');
        if (it.lyrics) head[0].lyrics = it.lyrics;
        items.push(...head);
        const tail = eventsOf(it.kind === 'rest', pitches, tailQ, it.notes[0]?.tie === 'start');
        if (voice.fermatas.has(it)) voice.fermatas.add(tail[tail.length - 1]);
        queue.splice(qi, 1, ...tail);
        used = budget;
        break;
      }
      if (voice.fermatas.has(it)) measures[b].fermata = true;
      const nav = voice.navs.get(it);
      if (nav === 'toCoda') {
        // The sign at a bar's START means the jump replaces that bar (skip
        // semantics); attached MID-bar it means "play to here, then jump" —
        // the exit lands on the next barline.
        const target = used <= 1e-6 ? b : Math.min(b + 1, measures.length - 1);
        if (measures[target]) measures[target].coda = true;
      } else if (nav) {
        if (measures[b]) measures[b].directive = nav;
      }
      items.push(it);
      used += q;
      qi++;
    }
    partMeasures.push([{ id: uid('obv'), items }]);
  }
  let melodyLeftoverQ = 0;
  for (let k = qi; k < queue.length; k++) melodyLeftoverQ += itemQuarters(queue[k]);

  // Pair after barline splitting, so generated fragments and authored
  // chains receive identical incoming stops, including tuplet members.
  completeIncomingTies(partMeasures);

  if (measures.length > 0) measures[0].keySig = voice.header.key;

  const score: Score = {
    title: attrs['title'] ?? 'Untitled',
    ...(attrs['composer'] ? { composer: attrs['composer'] } : {}),
    ...(attrs['piece'] ? { style: attrs['piece'] } : {}),
    ...(voice.header.bpm ? { tempo: voice.header.bpm } : {}),
    ...(voice.header.tempoText ? { tempoText: voice.header.tempoText } : {}),
    form: measures,
    parts: [
      {
        id: 'part:lead',
        name: 'Lead',
        instrument: 'lead',
        role: 'lead',
        staves: [{ id: 'staff:lead', clef: 'treble8vb', staffType: 'standard' }],
        measures: partMeasures.map((voices, k) => ({ id: `obpm-${k}`, voices })),
      },
    ],
    spanners: [],
  };
  return {
    title: attrs['title'] ?? 'Untitled',
    composer: attrs['composer'],
    style: attrs['style'],
    piece: attrs['piece'],
    version: def,
    score: normalizeScore(score),
    melodyLeftoverQ,
  };
}

function collectLyrics(
  src: string,
  def: string,
): { text: string; hyphen?: boolean; skip?: boolean }[][] {
  const verses: { text: string; hyphen?: boolean; skip?: boolean }[][] = [];
  for (const name of [`Lyrics${def}`, `Lyricsmore${def}`, `Lyricsmoremore${def}`]) {
    const block = makoSection(src, name);
    if (block) verses.push(parseLyrics(block));
  }
  return verses;
}

/**
 * Attach verse syllables to melody notes, LilyPond-style: one syllable per
 * note that STARTS a sound (tie continuations skip; `_` placeholders skip a
 * note; slur melismas are approximated by the `__` extenders having already
 * been dropped in parseLyrics).
 */
function assignLyrics(
  items: VoiceItem[],
  verses: { text: string; hyphen?: boolean; skip?: boolean }[][],
): void {
  if (verses.length === 0) return;
  const events = flatEvents(items);
  for (let v = 0; v < verses.length; v++) {
    const verse = verses[v];
    let vi = 0;
    let prevTied = false;
    for (const ev of events) {
      if (vi >= verse.length) break;
      if (ev.kind !== 'note' || ev.notes.length === 0) continue;
      if (prevTied) {
        prevTied = ev.notes[0]?.tie === 'start';
        continue;
      }
      prevTied = ev.notes[0]?.tie === 'start';
      const syl = verse[vi++];
      if (syl.skip || syl.text === '') continue;
      const lyr: (LyricSyllable | undefined)[] = ev.lyrics ?? [];
      // pad explicitly — writing lyr[1] on [] makes a SPARSE array whose
      // holes survive .map and NaN downstream width maths
      while (lyr.length < v) lyr.push(undefined);
      lyr[v] = { text: syl.text, ...(syl.hyphen ? { hyphen: true } : {}) };
      ev.lyrics = lyr;
    }
  }
}
